import { z } from "zod";
import {
  SALES_MODULES,
  type SalesContactDto,
  type SalesModuleKey,
  type SalesPrices,
  type SalesSeller,
} from "./sales.js";

/**
 * Offertes voor de verkoop van Klantpunt (Platformbeheer › Offertes, docs/VERKOOP.md stap 2).
 * De invoer is klein (klant, gebouwen, modules, korting); `computeQuote` rekent de regels en
 * totalen uit, zodat editor, PDF en server hetzelfde bedrag tonen.
 */

export const SALES_QUOTE_STATUSES = ["draft", "sent", "accepted", "declined"] as const;
export type SalesQuoteStatus = (typeof SALES_QUOTE_STATUSES)[number];
export const SALES_QUOTE_STATUS_LABELS: Record<SalesQuoteStatus | "expired", string> = {
  draft: "Concept",
  sent: "Verstuurd",
  accepted: "Akkoord",
  declined: "Afgewezen",
  expired: "Verlopen",
};

/** Verstuurd maar de geldigheid is voorbij: verlopen (alleen weergave). */
export function quoteDisplayStatus(status: SalesQuoteStatus, validUntil: string | null, today: string) {
  return status === "sent" && validUntil && validUntil < today ? ("expired" as const) : status;
}

const MODULE_KEYS = SALES_MODULES.map((m) => m.key) as [SalesModuleKey, ...SalesModuleKey[]];
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Ongeldige datum");
const count = (max: number) => z.number().int().min(0).max(max);
const text = (max: number) => z.string().trim().max(max);

export const salesQuoteModuleSchema = z.object({ key: z.enum(MODULE_KEYS), buildings: count(10_000) });

export const salesQuoteInputSchema = z.object({
  /** Aan wie de offerte gericht is (contactpersoon van de klant). */
  contactId: z.uuid().nullable().default(null),
  /** Titel op de voorkant; leeg = "Grip op N gebouwen". */
  title: text(120).default(""),
  /** Persoonlijke inleiding op de samenvatting; leeg = standaardtekst. */
  intro: text(1500).default(""),
  buildings: count(10_000).min(1).default(1),
  clients: count(1_000).default(0),
  /** All-in per gebouw in plaats van basis + losse modules. */
  allIn: z.boolean().default(false),
  modules: z.array(salesQuoteModuleSchema).max(SALES_MODULES.length).default([]),
  discountPct: z.number().min(0).max(100).default(0),
  /** Hoe lang de korting geldt; 0 = de hele looptijd. */
  discountMonths: count(120).default(12),
  setup: z.boolean().default(true),
  setupWaived: z.boolean().default(false),
  termMonths: count(120).min(1).default(12),
  startOn: isoDate.nullable().default(null),
  validUntil: isoDate.nullable().default(null),
  /** Mag de klant akkoord geven zonder PO-nummer (stap 3). */
  allowNoPo: z.boolean().default(true),
});
export type SalesQuoteInput = z.infer<typeof salesQuoteInputSchema>;

export const salesQuoteCreateSchema = z.object({
  customerId: z.uuid(),
  input: salesQuoteInputSchema.partial().default({}),
});
export type SalesQuoteCreateBody = z.input<typeof salesQuoteCreateSchema>;

export const salesQuoteUpdateSchema = z.object({
  input: salesQuoteInputSchema.optional(),
  status: z.enum(SALES_QUOTE_STATUSES).optional(),
  /** Concept opnieuw rekenen met de huidige prijslijst. */
  refreshPrices: z.boolean().optional(),
});
export type SalesQuoteUpdateBody = z.input<typeof salesQuoteUpdateSchema>;

export interface SalesQuoteLine {
  key: string;
  label: string;
  description: string;
  qty: number;
  /** Prijs per stuk in centen (0 bij een kortingsregel). */
  price: number;
  /** Totaal in centen; negatief bij korting. */
  total: number;
}

export interface SalesQuoteTotals {
  monthlyLines: SalesQuoteLine[];
  monthlyDiscount: SalesQuoteLine | null;
  onceLines: SalesQuoteLine[];
  /** Lijstprijs per maand (voor korting). */
  subtotal: number;
  discount: number;
  /** Per maand excl. btw, na korting. */
  monthly: number;
  vat: number;
  /** Eerste jaar (12 maanden), rekening houdend met de duur van de korting. */
  firstYear: number;
  /** Eenmalig, na eventuele kwijtschelding. */
  once: number;
  /** Modules in dit voorstel (bij all-in: alle). */
  included: SalesModuleKey[];
}

export const VAT_RATE = 0.21;

export function quoteTitle(input: Pick<SalesQuoteInput, "title" | "buildings">): string {
  if (input.title) return input.title;
  return `Grip op ${input.buildings} ${input.buildings === 1 ? "gebouw" : "gebouwen"}`;
}

/** Regels en totalen van een offerte. Bedragen in centen, excl. btw. */
export function computeQuote(input: SalesQuoteInput, prices: SalesPrices): SalesQuoteTotals {
  const n = input.buildings;
  const monthlyLines: SalesQuoteLine[] = [];
  const line = (key: string, label: string, description: string, qty: number, price: number) =>
    monthlyLines.push({ key, label, description, qty, price, total: qty * price });

  line("org", "Organisatie", "Platform, onbeperkt gebruikers, support en updates", 1, prices.org);
  if (input.clients > 0)
    line("client", "Klanten", "Opdrachtgevers met eigen omgeving en rapporten", input.clients, prices.client);
  let included: SalesModuleKey[];
  if (input.allIn) {
    line("allin", "All-in per gebouw", "Basis en alle modules", n, prices.allin);
    included = SALES_MODULES.map((m) => m.key);
  } else {
    line(
      "building",
      "Basis per gebouw",
      "Gebouwen, bevindingen, assets, adresboek, inbox, planning",
      n,
      prices.building,
    );
    included = [];
    for (const m of SALES_MODULES) {
      const q = Math.min(input.modules.find((x) => x.key === m.key)?.buildings ?? 0, n);
      if (q <= 0) continue;
      included.push(m.key);
      line(m.key, m.label, q < n ? `${q} van de ${n} gebouwen` : "", q, prices[m.key]);
    }
  }
  const subtotal = monthlyLines.reduce((s, l) => s + l.total, 0);
  const discount = Math.round((subtotal * input.discountPct) / 100);
  const pct = formatPct(input.discountPct);
  const monthlyDiscount: SalesQuoteLine | null =
    discount > 0
      ? {
          key: "discount",
          label: `Introductiekorting ${pct}`,
          description: input.discountMonths ? `Eerste ${input.discountMonths} maanden` : "Gedurende de looptijd",
          qty: 0,
          price: 0,
          total: -discount,
        }
      : null;
  const monthly = subtotal - discount;
  const discountedMonths = input.discountMonths ? Math.min(input.discountMonths, 12) : 12;
  const firstYear = monthly * discountedMonths + subtotal * (12 - discountedMonths);

  const onceLines: SalesQuoteLine[] = [];
  if (input.setup && prices.setup > 0) {
    onceLines.push({
      key: "setup",
      label: "Inrichting en import",
      description: "Plattegronden, assets en keuringen uit Excel of Assetlog",
      qty: 1,
      price: prices.setup,
      total: prices.setup,
    });
    if (input.setupWaived)
      onceLines.push({
        key: "setupWaived",
        label: input.termMonths >= 12 ? "Kosteloos bij jaarcontract" : "Kosteloos",
        description: "",
        qty: 0,
        price: 0,
        total: -prices.setup,
      });
  }
  const once = onceLines.reduce((s, l) => s + l.total, 0);
  return {
    monthlyLines,
    monthlyDiscount,
    onceLines,
    subtotal,
    discount,
    monthly,
    vat: Math.round(monthly * VAT_RATE),
    firstYear,
    once,
    included,
  };
}

const dayLongNl = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });

/** Voorwaarden in het kort: dezelfde tekst in de PDF en op de akkoordpagina. */
export function quoteTerms(input: SalesQuoteInput, seller: string): [string, string][] {
  return [
    [
      "Looptijd.",
      `${input.termMonths} maanden vanaf de ingangsdatum, daarna steeds met een jaar verlengd. Opzeggen kan tot één maand voor het einde.`,
    ],
    [
      "Facturatie.",
      `Maandelijks vooraf${input.allowNoPo ? ", met het PO-nummer of jullie kenmerk op de factuur" : ", met het PO-nummer op de factuur"}. Betaling binnen 14 dagen.`,
    ],
    [
      "Bijboeken.",
      "Gebouwen, klanten en modules kunnen tussentijds bij; ze worden vanaf die maand gefactureerd tegen de prijzen in dit voorstel.",
    ],
    [
      "Gegevens.",
      "Jullie blijven eigenaar van alle gegevens. Verwerking volgens de verwerkersovereenkomst, die bij het akkoord hoort.",
    ],
    [
      "Algemene voorwaarden.",
      `Op dit voorstel zijn de algemene voorwaarden van ${seller} van toepassing (klantpunt.io/voorwaarden).`,
    ],
    [
      "Geldigheid.",
      `${input.validUntil ? `Dit voorstel is geldig tot ${dayLongNl(input.validUntil)}. ` : ""}Prijzen in euro's, exclusief btw.`,
    ],
  ];
}

export const formatPct = (p: number) => `${String(p).replace(".", ",")}%`;

export interface SalesQuoteRow {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  status: SalesQuoteStatus;
  title: string;
  buildings: number;
  /** Per maand excl. btw, na korting. */
  monthly: number;
  validUntil: string | null;
  /** Eerste keer geopend door de klant (akkoordpagina), sinds het laatst versturen. */
  viewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SalesQuoteDetail extends SalesQuoteRow {
  input: SalesQuoteInput;
  /** Prijzen zoals vastgelegd bij het maken (of laatst bijgewerkt in concept). */
  prices: SalesPrices;
  token: string;
  sentAt: string | null;
  /** Versie: telt op bij elke keer versturen; het akkoord hoort bij één versie. */
  version: number;
  customer: { id: string; name: string; city: string | null; type: string | null };
  contacts: SalesContactDto[];
  seller: SalesSeller;
  createdByName: string | null;
  /** Online akkoord (stap 3), alleen bij status Akkoord via de akkoordpagina. */
  agreement: SalesAgreementDto | null;
}

// ---------- online akkoord (docs/VERKOOP.md stap 3) ----------

/** IBAN zonder spaties, in hoofdletters. */
export const normalizeIban = (v: string) => v.replace(/[\s.-]+/g, "").toUpperCase();
/** NL91 ABNA 0417 1643 00 */
export const formatIban = (v: string) =>
  normalizeIban(v)
    .replace(/(.{4})/g, "$1 ")
    .trim();
/** Alleen land, bank en laatste twee cijfers: NL91 ABNA 0417 1643 00 → NL•• ABNA •••• ••00. */
export const maskIban = (v: string) => {
  const s = normalizeIban(v);
  return `${s.slice(0, 2)}•• ${s.slice(4, 8)} •••• ••${s.slice(-2)}`;
};

/** IBAN-controle (mod 97, ISO 13616). Nederlandse nummers hebben 18 tekens. */
export function ibanValid(v: string): boolean {
  const s = normalizeIban(v);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s)) return false;
  if (s.startsWith("NL") && s.length !== 18) return false;
  const r = (s.slice(4) + s.slice(0, 4)).replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  let m = 0;
  for (const ch of r) m = (m * 10 + Number(ch)) % 97;
  return m === 1;
}

export const salesAcceptSchema = z
  .object({
    /** De versie die de klant zag; is de offerte intussen opnieuw verstuurd, dan opnieuw laden. */
    version: z.number().int().min(1),
    name: text(120).min(2, "Vul je naam in"),
    role: text(120).default(""),
    email: z.email("Vul een geldig e-mailadres in").max(200),
    invoiceEmail: z.union([z.email("Vul een geldig e-mailadres in").max(200), z.literal("")]).default(""),
    billingAddress: text(400).default(""),
    noPo: z.boolean().default(false),
    poNumber: text(60).default(""),
    /** Eigen kenmerk op de factuur als er geen PO is. */
    reference: text(100).default(""),
    /** Budget op het PO in centen, excl. btw (optioneel). */
    poBudget: z.number().int().min(0).max(2_000_000_000).nullable().default(null),
    poUntil: isoDate.nullable().default(null),
    iban: z.string().max(50).refine(ibanValid, "Dit rekeningnummer klopt niet. Controleer de cijfers."),
    ibanHolder: text(120).default(""),
    agree: z.literal(true, "Ga akkoord met het voorstel en de voorwaarden"),
  })
  .superRefine((v, ctx) => {
    if (!v.noPo && v.poNumber.length < 2)
      ctx.addIssue({ code: "custom", path: ["poNumber"], message: "Vul jullie inkoopnummer in" });
  });
export type SalesAcceptBody = z.input<typeof salesAcceptSchema>;

/** Wat de klant terugziet na het akkoord (zonder volledig IBAN). */
export interface SalesAgreementReceipt {
  number: string;
  version: number;
  signerName: string;
  signerRole: string;
  acceptedAt: string;
  noPo: boolean;
  poNumber: string;
  reference: string;
  poBudget: number | null;
  poUntil: string | null;
  ibanMasked: string;
  monthly: number;
  startOn: string | null;
}

/** Het vastgelegde akkoord, voor Platformbeheer. */
export interface SalesAgreementDto extends SalesAgreementReceipt {
  id: string;
  signerEmail: string;
  invoiceEmail: string;
  billingAddress: string;
  iban: string;
  ibanHolder: string;
  ip: string | null;
  userAgent: string | null;
}

/**
 * Openbare akkoordpagina /offerte/:token. `preview` = een concept, alleen voor platformbeheerders
 * (tekenen kan dan niet).
 */
export type PublicQuoteState = "open" | "accepted" | "declined" | "expired" | "preview";
export interface PublicQuoteDto {
  state: PublicQuoteState;
  /** Offerte zoals de klant hem ziet (alleen de eigen contactpersoon, geen interne gegevens). */
  quote: SalesQuoteDetail;
  /** Ontbrekende verkopergegevens: dan kan nog niet getekend worden. */
  sellerIncomplete: boolean;
  receipt: SalesAgreementReceipt | null;
}

/** Teksten per module voor de offerte: wat je ermee kunt en hoe het werkt. */
export interface SalesModuleCopy {
  name: string;
  line: string;
  can: string[];
  how: string[];
}

export const SALES_BASE_COPY: SalesModuleCopy = {
  name: "Gebouwen, bevindingen en assets",
  line: "Eén plek voor alles wat er in en aan je gebouwen gebeurt. Op de tekening, met foto, op elke telefoon.",
  can: [
    "Plattegronden per verdieping met assets en bevindingen als pin",
    "Bevindingen met foto, prioriteit P1–P4 en status tot en met opgelost",
    "Assets met QR-sticker: scannen en je ziet de hele historie",
    "Inbox voor meldingen van gebruikers en huurders, zonder account",
    "Adresboek met leveranciers, storingsnummers en erkenningen",
    "Planning over alle gebouwen en PDF-rapport in één klik",
  ],
  how: [
    "Je zet de plattegronden en assets klaar (wij helpen met de import).",
    "Iedereen meldt met de telefoon of via de QR-code; de servicedesk beoordeelt in de Inbox.",
    "Taken gaan naar de leverancier, worden afgerond met foto en komen in het rapport.",
  ],
};

export const SALES_MODULE_COPY: Record<SalesModuleKey, SalesModuleCopy> = {
  keuringen: {
    name: "Keuringen en onderhoud",
    line: "Nooit meer een verlopen keuring. Klantpunt weet wat wanneer moet en wie het doet.",
    can: [
      "Verplichtingen per asset: wettelijk, norm of eigen afspraak",
      "Vervaldatums met seintje vooraf en overzicht Verlopen / Binnenkort",
      "Vaste uitvoerder uit het adresboek, afspraak mailen met agenda-uitnodiging",
      "Certificaat vastleggen; het belandt vanzelf in het dossier",
    ],
    how: [
      "Kies per asset de keuringen uit de kennisbank (liften, NEN 3140, brandmelding).",
      "Klantpunt plant de volgende datum en waarschuwt op tijd.",
      "De uitvoerder legt de uitvoering vast, met certificaat en eventuele bevindingen.",
    ],
  },
  mjop: {
    name: "MJOP",
    line: "Het meerjarenonderhoudsplan dat zichzelf bijhoudt, rechtstreeks uit je assets.",
    can: [
      "Vervangingsjaren en kosten per asset, met indexering",
      "Begroting per jaar en per gebouw, met dashboard",
      "Reservering volgens de saldomethode (ook voor VvE's)",
      "Kosten van keuringen en onderhoud lopen automatisch mee",
    ],
    how: [
      "Assets krijgen een levensduur en vervangingswaarde.",
      "Klantpunt rekent de begroting voor 10 tot 30 jaar uit.",
      "Je stuurt bij met conditie en schuift posten met een reden.",
    ],
  },
  conditie: {
    name: "Conditiemeting NEN 2767",
    line: "Inspecteren met de telefoon in de hand, met een score die het MJOP direct gebruikt.",
    can: [
      "Inspecteren per bouwdeel met gebrekenlijsten",
      "Conditiescore 1–6 per asset, bouwdeel en gebouw",
      "Foto's en gebreken direct op de tekening",
      "Voorstel voor het MJOP uit de gemeten conditie",
    ],
    how: [
      "Start een inspectie per gebouw en loop de bouwdelen langs.",
      "Kies gebreken uit de lijst; Klantpunt berekent de score.",
      "Neem de voorstellen over in het MJOP of maak er een taak van.",
    ],
  },
  dossier: {
    name: "Dossier",
    line: "Alle documenten en tekeningen van een gebouw, altijd de geldige versie.",
    can: [
      "Vaste rubrieken: vergunningen, veiligheid, energie, installaties",
      "Versies, Geldig tot en herinnering voordat iets verloopt",
      "Zichtbaarheid: intern, klant ziet mee of vertrouwelijk",
      "Delen via een link die je zelf intrekt",
    ],
    how: [
      "Sleep documenten erin; de soort bepaalt de geldigheid.",
      "Koppel ze aan assets en ruimtes.",
      "Klantpunt waarschuwt voor verlopen documenten.",
    ],
  },
  schermen: {
    name: "Informatieschermen",
    line: "Een tv in de lobby of het beheerkantoor die laat zien wat er speelt.",
    can: [
      "Open meldingen, planning van deze week en meld-QR",
      "Thema licht of donker, nachtstand",
      "Koppelen met een code, intrekken met één klik",
    ],
    how: [
      "Maak een scherm aan bij het gebouw.",
      "Open de link op de tv of voer de koppelcode in.",
      "Het scherm ververst zichzelf.",
    ],
  },
  materialen: {
    name: "Materialen",
    line: "Weten wat er op voorraad ligt en wat er bij een klus is gebruikt.",
    can: [
      "Artikellijst met leverancier en prijs",
      "Reservevoorraad per gebouw met minimum",
      "Materialen bij onderhoud en taken",
      "Afboeken bij afronden, met seintje bij te weinig voorraad",
    ],
    how: [
      "Zet de artikelen en voorraad per gebouw klaar.",
      "Koppel materialen aan onderhoud of een taak.",
      "Bij afronden boekt Klantpunt de voorraad af.",
    ],
  },
  meters: {
    name: "Meterstanden",
    line: "Opnemen met de telefoon, afwijkingen direct zien.",
    can: [
      "Opnamerondes per gebouw met foto van de meter",
      "Controle op afwijkingen ten opzichte van vorige standen",
      "Verbruik vergelijken per periode",
      "Export naar Excel",
    ],
    how: [
      "Leg de meters vast bij het gebouw.",
      "Loop de ronde met de telefoon en maak een foto per meter.",
      "Klantpunt rekent het verbruik uit en markeert afwijkingen.",
    ],
  },
  contracten: {
    name: "Contracten",
    line: "Onderhoudscontracten met wat ze dekken, zodat je ziet wat nog los is.",
    can: [
      "Onderhoudscontracten per leverancier met looptijd en opzegtermijn",
      "Posten met prijzen en gedekte uitvoeringen",
      "Overzicht van wat nog zonder contract is",
      "Seintje voor verlengen of opzeggen",
    ],
    how: [
      "Leg het contract vast met de posten.",
      "Koppel de posten aan keuringen en onderhoud.",
      "Klantpunt laat zien wat gedekt is en wat niet.",
    ],
  },
};
